import React, { useEffect, useState } from 'react';
import { Star, Camera, X } from 'lucide-react';
import { fetchReviews, apiPostReview } from '../api/client';
import { useStore } from '../context/StoreContext';
import { useLocale } from '../context/LocaleContext';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';

const StarInput = ({ value, onChange, size = 22 }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)}
        className="hover:scale-110 transition-transform" aria-label={`${n} star`}>
        <Star size={size} className={n <= value ? 'fill-[#22c55e] text-[#22c55e]' : 'text-gray-300'} />
      </button>
    ))}
  </div>
);

const StarShow = ({ value }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={14} className={n <= value ? 'fill-[#22c55e] text-[#22c55e]' : 'text-gray-300'} />
    ))}
  </div>
);

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const ReviewsSection = ({ productId }) => {
  const [reviews, setReviews] = useState({ items: [], avg: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [photo, setPhoto] = useState(null); // {base64, name}
  const [posting, setPosting] = useState(false);
  const { user } = useStore();
  const { t } = useLocale();

  const load = async () => {
    try {
      setLoading(true);
      const data = await fetchReviews(productId);
      setReviews(data);
    } catch (err) { console.warn('reviews fetch', err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [productId]);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast({ title: 'Photo too large', description: 'Max 2 MB' });
      return;
    }
    const b64 = await readFileAsBase64(f);
    setPhoto({ base64: b64, name: f.name });
  };

  const submit = async () => {
    if (rating < 1 || comment.trim().length < 3) {
      toast({ title: 'Please add a rating and a short comment' });
      return;
    }
    setPosting(true);
    try {
      await apiPostReview(productId, {
        rating,
        title: title.trim() || null,
        comment: comment.trim(),
        photo_base64: photo?.base64 || null,
        user_id: user?.email || 'guest',
        user_name: user?.name || 'Anonymous'
      });
      toast({ title: 'Review posted!' });
      setShowForm(false);
      setRating(0); setTitle(''); setComment(''); setPhoto(null);
      await load();
    } catch (err) {
      toast({ title: 'Could not post review', description: err.message });
    } finally {
      setPosting(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 my-14">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl md:text-3xl font-black">CUSTOMER REVIEWS</h2>
          <div className="h-1 w-14 bg-[#22c55e] mt-2"></div>
          {reviews.count > 0 && (
            <div className="flex items-center gap-2 mt-3">
              <StarShow value={Math.round(reviews.avg)} />
              <span className="text-sm font-semibold">{reviews.avg}</span>
              <span className="text-sm text-gray-500">/ 5 · {reviews.count} {t('reviews')}</span>
            </div>
          )}
        </div>
        <Button onClick={() => setShowForm((s) => !s)} className="bg-[#22c55e] hover:bg-[#16a34a] font-bold tracking-wider">
          {showForm ? 'CLOSE' : 'WRITE A REVIEW'}
        </Button>
      </div>

      {showForm && (
        <div className="bg-gray-50 border rounded p-5 mb-6">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-bold tracking-wider text-gray-700 mb-2">YOUR RATING *</label>
              <StarInput value={rating} onChange={setRating} size={28} />
              <label className="block text-xs font-bold tracking-wider text-gray-700 mb-2 mt-4">TITLE (OPTIONAL)</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Loved it!" className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-[#22c55e]" />
              <label className="block text-xs font-bold tracking-wider text-gray-700 mb-2 mt-4">COMMENT *</label>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4}
                placeholder="Tell others what you think..." className="w-full border rounded px-3 py-2 text-sm outline-none focus:border-[#22c55e]" />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-wider text-gray-700 mb-2">ADD A PHOTO (OPTIONAL, MAX 2MB)</label>
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded p-6 cursor-pointer hover:border-[#22c55e] transition min-h-[180px]">
                {photo ? (
                  <div className="relative w-full">
                    <img src={photo.base64} alt="preview" className="w-full h-40 object-cover rounded" />
                    <button type="button" onClick={(e) => { e.preventDefault(); setPhoto(null); }} className="absolute top-1 right-1 bg-white rounded-full p-1 shadow"><X size={14} /></button>
                  </div>
                ) : (
                  <>
                    <Camera className="text-gray-400" size={32} />
                    <p className="text-xs text-gray-500 mt-2">Click to upload</p>
                  </>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
              </label>
              <Button onClick={submit} disabled={posting} className="w-full mt-4 h-11 bg-[#22c55e] hover:bg-[#16a34a] font-bold tracking-wider">
                {posting ? 'POSTING...' : 'POST REVIEW'}
              </Button>
              {!user && <p className="text-[11px] text-gray-500 mt-2">You'll be posting as "Anonymous". Log in to show your name.</p>}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500 text-sm">Loading reviews...</div>
      ) : reviews.items.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded">
          <p className="text-gray-500">No reviews yet. Be the first to leave one!</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {reviews.items.map((r) => (
            <div key={r.review_id} className="border rounded p-4 bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold text-sm">{r.user_name}</div>
                  <StarShow value={r.rating} />
                </div>
                <span className="text-[11px] text-gray-400">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.title && <h4 className="font-semibold mt-2 text-sm">{r.title}</h4>}
              <p className="text-sm text-gray-700 mt-1">{r.comment}</p>
              {r.photo_base64 && (
                <img src={r.photo_base64} alt="review" className="mt-3 w-full max-h-64 object-cover rounded" />
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ReviewsSection;
